import { Separator } from "@/components/ui/separator.tsx";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet.tsx";
import { cn } from "@/lib/utils";
import { Cross1Icon } from "@radix-ui/react-icons";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { ChevronLeft } from "lucide-react";
import { ReactNode } from "react";
import { DialogProps } from "vaul";

interface StyledDrawerProps {
  children: ReactNode;
  title: ReactNode;
  trigger?: ReactNode;
  className?: string;
  closeButton?: ReactNode;
  footer?: ReactNode;
  sheetProps?: DialogProps;
  description?: ReactNode;
  arrowClose?: boolean;
  maxWidth?: string; // e.g., "sm:max-w-5xl", "w-[800px]", etc.
}

const StyledDrawer = ({
  arrowClose = false,
  title,
  children,
  trigger,
  className,
  closeButton,
  footer,
  sheetProps,
  description,
  maxWidth,
}: StyledDrawerProps) => {
  return (
    <Sheet {...sheetProps}>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent
        side="right"
        showPrimitiveCross={false}
        className={cn(
          "max-h-screen flex flex-col gap-0",
          className,
          maxWidth || "sm:max-w-sm",
        )}
      >
        {title && (
          <VisuallyHidden>
            <SheetTitle>{title}</SheetTitle>
          </VisuallyHidden>
        )}
        <div className="w-full self-stretch justify-start items-center gap-4 inline-flex pb-4 p-6">
          <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
            <div className="self-stretch text-[#0c111d] text-lg font-semibold font-['Raleway'] leading-normal flex items-center">
              {arrowClose && (
                <SheetClose>
                  <ChevronLeft className="h-6 w-6 relative mr-4" />
                </SheetClose>
              )}
              {title}
            </div>
          </div>
          <SheetClose>
            {closeButton ? (
              closeButton
            ) : (
              <Cross1Icon className="h-4 w-4 relative text-gray-400" />
            )}
          </SheetClose>
        </div>
        <Separator />
        <div className="pt-4 flex flex-col flex-1 overflow-y-auto p-6">
          {children}
        </div>
        <SheetFooter>{footer}</SheetFooter>
        {description && (
          <VisuallyHidden>
            <SheetDescription>{description}</SheetDescription>
          </VisuallyHidden>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default StyledDrawer;
